import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { compareNames } from '@pubstack/common/src/sort'
import { FC, PropsWithChildren, ReactNode } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { ElevationLevel } from '~/assets/style/tokens'
import { Icon } from '~/components/Icon'
import { Skeleton } from '~/components/Skeleton'
import { WithClassName } from '~/types/utils'
import TableCell from './TableCell'
import TableRow from './TableRow'

type SortOrder = 'ascending' | 'descending' | 'none'

interface BaseColumn {
  name: ReactNode
  /**
   * A width for this column, as any CSS length — `'120px'`, `'15%'`.
   *
   * Opt-in, and per table: the moment *any* column declares one, the table switches to `table-layout:
   * fixed` and the widths are declared once in a `<colgroup>` instead of being re-derived from content.
   * That is what stops a paginated table from re-laying itself out on every page — under the default
   * `auto` layout the browser measures the rows it currently has, so the same column is one width on
   * page 1 and another on page 2, purely because the names on it are longer.
   *
   * Give a width to the columns whose content is enumerable (a status, an env, a version), and leave the
   * free-text ones alone: under fixed layout the columns without a width split what is left over, which
   * is the behaviour a name column wants anyway.
   *
   * A table that declares none of these keeps the old `auto` layout untouched.
   */
  width?: string
}

export interface Column extends BaseColumn {
  isSortable: false
}

/**
 * Here you can define your own sort algorithm on the column which will be handled by handleTableSort() function
 */
export interface SortableColumn<T = any> extends BaseColumn {
  isSortable: true
  order: SortOrder
  attributeSort: keyof T
  sort?: (a: T, b: T, order: SortOrder) => number
}

export type TableColumns<T = any> = (Column | SortableColumn<T>)[]

export const onColumnSort = <T,>(columns: TableColumns<T>, column: SortableColumn<T>, setColumns: (columns: TableColumns<T>) => void) => {
  setColumns(
    columns.map((c) => {
      if (c === column) {
        return { ...c, order: c.order === 'ascending' ? 'descending' : 'ascending' }
      } else {
        return c.isSortable ? { ...c, order: 'none' } : c
      }
    })
  )
}

const sortByType = (a: any, b: any): number => {
  switch (typeof a) {
    case 'number':
    case 'boolean':
      return Number(b) - Number(a)
    case 'string':
      // `String(b)`: `handleTableSort` hands over `false` for a missing value.
      return compareNames(a, String(b))
  }
  return 0
}

/**
 * Return a sorted array of elements based on columns configuration
 * @param columns
 * @param elements
 */
export const handleTableSort = <T,>(columns: TableColumns<T>, elements: T[] | undefined): T[] => {
  const sortedColumn = (columns.find((c) => c.isSortable && c.order !== 'none') || columns[0]) as SortableColumn<T>
  return [...(elements || [])].sort((a, b) =>
    sortedColumn.sort
      ? sortedColumn.sort(a, b, sortedColumn.order) * (sortedColumn.order === 'ascending' ? 1 : -1)
      : (sortedColumn.order === 'ascending' ? 1 : -1) * sortByType(a[sortedColumn.attributeSort] ?? false, b[sortedColumn.attributeSort] ?? false)
  )
}

// TODO tmu 2022-11-15 type correctly to disallow passing searchAttributes pointing to objects
/**
 * Return a filtered array containing values matching a search over specific stringable attributes
 * @param data array of objects to filter
 * @param search string value currently searched
 * @param searchAttributes object's properties on which we want to search
 */
export const handleTableSearch = <T extends { [key in keyof T]: object | string | number | boolean | null | undefined }>(data: T[], search: string, searchAttributes: (keyof T)[]): T[] => {
  return data.filter((d) => {
    return searchAttributes.some((attr) => {
      const value = d[attr as keyof T]
      if (value) {
        return value.toString().toLowerCase().includes(search.toLowerCase())
      }
    })
  })
}

export const handleTableSearchAndSort = <T extends { [key in keyof T]: object | string | number | boolean | null | undefined }>(
  columns: TableColumns<T>,
  data: T[],
  search: string,
  searchAttributes: (keyof T)[]
): T[] => {
  return handleTableSearch(handleTableSort(columns, data), search, searchAttributes)
}

export const EmptyTable = styled.div`
  display: flex;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 40px;
`

const Th = styled.th<{ isSortable?: boolean; top: number }>`
  background: ${Colors.White};
  position: sticky;
  top: ${(props) => props.top}px;
  ${ElevationLevel.low}
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'auto')};
  padding: 0;
  margin: 0;
  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    box-sizing: border-box;
    border-top: 1px solid ${Colors.Platinum};
    border-bottom: 1px solid ${Colors.Platinum};
    /* Absolutely positioned, so it paints above the header's own in-flow content in stacking order —
       without this, it silently swallows clicks meant for anything interactive a header cell renders
       (a button, a menu trigger), redirecting them to the cell's own sort handler instead. It only ever
       draws border lines, so opting it out of hit-testing changes nothing about how it looks. */
    pointer-events: none;
  }
  &:last-child {
    border-radius: 0 4px 0 0;
    border-top: none;
    border-bottom: none;
  }
  &:last-child:before {
    border-right: 1px solid ${Colors.Platinum};
    border-radius: 0 4px 0 0;
  }
  &:first-of-type {
    border-radius: 4px 0 0 0;
    border-top: none;
  }
  &:first-of-type:before {
    border-left: 1px solid ${Colors.Platinum};
    border-radius: 4px 0 0 0;
  }
`

const Tr = styled.tr<{ noWrapHeader?: boolean }>`
  padding: 0;
  margin: 0;
  ${({ noWrapHeader }) =>
    !!noWrapHeader &&
    css`
      white-space: nowrap;
    `}
`

const TableStyled = styled.table`
  border-collapse: collapse;
  position: relative;
  width: 100%;
  padding: 0;
  margin: 0;
  background: ${Colors.White};

  & ${TableRow} {
    & ${TableCell}:first-of-type:not(:only-of-type) {
      font-weight: bold;
    }
  }
`

const Thead = styled.thead`
  background: ${Colors.White};
`
const HeaderItem = styled.div<{ isSortable: boolean }>`
  display: flex;
  align-items: center;
  padding: 16px 12px;
  width: 100%;
  font-weight: 400;
  ${Fonts.colors.SlateGrey};
  ${Fonts.P1};

  ${({ isSortable }) =>
    isSortable &&
    css`
      cursor: pointer;
    `}
`

const HeaderItemName = styled.span`
  margin: 0;
  display: block;
`

const HeaderSortIcon = styled(Icon)`
  margin-left: 2px;
`

const TableContainer = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`

const fakeColumns = (columns: TableColumns, rowIndex: number, random: () => number) =>
  columns.map((_, i) => (
    <TableCell key={`cell-${i}-${rowIndex}`}>
      <Skeleton width={Math.floor(random() * 31 + 40) + '%'} />
    </TableCell>
  ))

type TableProps<T = any> = WithClassName & {
  isLoading: boolean
  headersTop?: number
  columns: TableColumns<T>
  onClickHeading?: (column: SortableColumn) => void
  noWrapHeader?: boolean
}
const BaseTable: FC<PropsWithChildren<TableProps>> = ({ children, headersTop = 0, className, noWrapHeader, ...props }) => {
  const seed = (s: number) => {
    return () => {
      s = Math.sin(s) * 10000
      return s - Math.floor(s)
    }
  }

  const random = seed(seed(seed(42)())())

  const headingClick = (column: Column | SortableColumn): void => {
    if (column.isSortable) {
      props.onClickHeading?.(column)
    }
  }

  const renderTh = (props.columns || []).map((column, i) => (
    <Th key={`row-${i}`} onClick={() => headingClick(column)} isSortable={column.isSortable} top={headersTop}>
      <HeaderItem isSortable={column.isSortable}>
        <HeaderItemName>{column.name}</HeaderItemName>
        {column.isSortable && <HeaderSortIcon name={column.order === 'ascending' ? 'organize_up' : column.order === 'descending' ? 'organize_down' : 'organize'} width={'18px'} />}
      </HeaderItem>
    </Th>
  ))
  const bodyLoading = new Array(5).fill(0).map((_, i) => <TableRow key={`row-${i}`}>{fakeColumns(props.columns, i, random)}</TableRow>)

  // Built from the same `columns` array as the headers and the skeleton, so the three cannot drift apart.
  // Widths are only honoured under `table-layout: fixed`, and fixed layout with no widths at all would
  // make every column equal — so the two go together, and a table that asked for neither gets neither.
  const columnWidths = (props.columns || []).map((column) => column.width)
  const hasColumnWidths = columnWidths.some((width) => !!width)

  return (
    <TableContainer className={className}>
      <TableStyled className={hasColumnWidths ? 'table-fixed' : undefined}>
        {hasColumnWidths && (
          <colgroup>
            {columnWidths.map((width, i) => (
              <col key={`col-${i}`} style={width ? { width } : undefined} />
            ))}
          </colgroup>
        )}
        <Thead>
          <Tr noWrapHeader={noWrapHeader}>{renderTh}</Tr>
        </Thead>
        <tbody>{props.isLoading ? bodyLoading : children}</tbody>
      </TableStyled>
    </TableContainer>
  )
}
const Table = styled(BaseTable)``
export default Table
